/**
 * `useTenantHash` — maintains the `#tid_${tenantId}` URL hash for every
 * authenticated route, matching process-runner's `renderCommonRouterView`
 * behaviour. Mirrors the same logic:
 *
 *   1. If the URL has no `#tid_…` hash and the signed-in user has a
 *      tenant id, append `#tid_${tenantId}` via `history.replaceState`
 *      (no router navigation; keeps current pathname + search).
 *   2. If the URL hash tenant id doesn't match the session's tenant id,
 *      bounce to the home route with `?invalidTid=${hashTid}&targetUrl=…`
 *      so a tenant-switch handler can take over (deferred for mobile —
 *      currently we just rewrite the hash to the session's tenant id).
 *
 * Re-runs on every location change so the hash survives back-button
 * navigations / programmatic `navigate()`.
 */

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  getTenantDetails,
  getTenantIdFromHash,
  isValidSession,
} from '@services/session'

export function useTenantHash(): void {
  const location = useLocation()

  useEffect(() => {
    if (!isValidSession()) return
    const sessionTid = getTenantDetails()?.tenantId
    if (!sessionTid) return

    const hashTid = getTenantIdFromHash(window.location.hash)
    if (!hashTid) {
      // No hash yet — append one without a router-level navigation.
      const desired = `${window.location.pathname}${window.location.search}#tid_${sessionTid}`
      window.history.replaceState(null, '', desired)
      return
    }
    if (hashTid !== sessionTid) {
      // Mismatch — re-anchor to the session tenant. Process-runner
      // shows an "invalid tid" picker; mobile keeps it simple by
      // rewriting to the truth source (session) and surfacing the
      // attempted tid as a query param for any future picker.
      const params = new URLSearchParams(window.location.search)
      params.set('invalidTid', hashTid)
      const next = `${window.location.pathname}?${params.toString()}#tid_${sessionTid}`
      window.history.replaceState(null, '', next)
    }
  }, [location.pathname, location.search, location.hash])
}
